'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

// Demo credentials
const DEMO_CREDENTIALS = {
  email: 'traveler@travelhub.io',
  password: 'TH_Demo2026!',
};

interface Props {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: Props) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setAuthError('');
    setIsLoading(true);

    // BACKEND: POST /api/auth/login with { email, password, rememberMe }
    await new Promise((r) => setTimeout(r, 1200));

    if (
      data.email === DEMO_CREDENTIALS.email &&
      data.password === DEMO_CREDENTIALS.password
    ) {
      router.push('/');
    } else {
      setAuthError(
        'Invalid credentials — use the demo account below to sign in.'
      );
      setIsLoading(false);
    }
  };

  const fillDemo = () => {
    setValue('email', DEMO_CREDENTIALS.email);
    setValue('password', DEMO_CREDENTIALS.password);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      {/* Auth Error */}
      {authError && (
        <div className="flex items-start gap-2 bg-negative-bg border border-negative/20 rounded-xl px-4 py-3">
          <Icon name="ExclamationTriangleIcon" size={16} className="text-negative flex-shrink-0 mt-0.5" />
          <p className="text-sm text-negative">{authError}</p>
        </div>
      )}

      {/* Email */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5" htmlFor="login-email">
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          className={`input-base w-full px-4 py-3 text-sm ${errors.email ? 'border-negative' : ''}`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs text-negative mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-600 text-foreground" htmlFor="login-password">
            Password
          </label>
          <button
            type="button"
            className="text-xs text-primary font-semibold hover:underline"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Your password"
            className={`input-base w-full px-4 py-3 pr-11 text-sm ${errors.password ? 'border-negative' : ''}`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-negative mt-1">{errors.password.message}</p>
        )}
      </div>

      {/* Remember Me */}
      <label className="flex items-center gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          className="checkbox-custom"
          {...register('rememberMe')}
        />
        <span className="text-sm text-foreground">Remember me for 30 days</span>
      </label>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-70"
        style={{ minHeight: '48px' }}
      >
        {isLoading ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In to TravelHub'
        )}
      </button>

      {/* Switch to signup */}
      <p className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          className="text-primary font-semibold hover:underline"
        >
          Create one free
        </button>
      </p>

      {/* Demo Credentials Box */}
      <div className="mt-4 border border-dashed border-primary/40 bg-secondary rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-700 text-primary uppercase tracking-wide">Demo Account</p>
          <button
            type="button"
            onClick={fillDemo}
            className="text-xs btn-primary px-3 py-1"
          >
            Use Demo
          </button>
        </div>
        <div className="space-y-1.5">
          {[
            { label: 'Email', value: DEMO_CREDENTIALS.email },
            { label: 'Password', value: DEMO_CREDENTIALS.password },
          ].map(({ label, value }) => (
            <div key={`demo-${label}`} className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground w-16 flex-shrink-0">{label}</span>
              <code className="text-xs font-mono text-foreground bg-card px-2 py-0.5 rounded flex-1 truncate">
                {value}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(value)}
                className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
                aria-label={`Copy ${label}`}
              >
                <Icon name="ClipboardDocumentIcon" size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}